import { useEffect, useRef, useCallback, RefObject } from "react";

interface UseScrollToTopOptions {
  /** Values that trigger a scroll when they change (like deps in useEffect). Default: [] */
  deps?: React.DependencyList;
  /** ScrollBehavior passed to scrollTo(). Default: "smooth" */
  behavior?: ScrollBehavior;
  /** Delay in ms before scrolling — useful after CSS transitions. Default: 0 */
  delay?: number;
  /** Set true to pause auto-scrolling without removing the hook. Default: false */
  disabled?: boolean;
  /** Ref to a scrollable container. If omitted, scrolls the window. */
  targetRef?: RefObject<HTMLElement>;
}

interface UseScrollToTopReturn {
  /** Call imperatively to scroll at any time */
  scrollToTop: () => void;
  /** Attach to a DOM element to scope scrolling to it */
  targetRef: RefObject<HTMLElement>;
}

/**
 * useScrollToTop
 *
 * Automatically scrolls to the top of the page (or a target element)
 * whenever the specified dependencies change — perfect for route changes,
 * tab switches, paginated lists, or any content swap.
 *
 * Usage examples
 * ─────────────────────────────────────────────────────────────────
 * // 1. Scroll window on route change (React Router)
 * import { useLocation } from "react-router-dom";
 * const { pathname } = useLocation();
 * useScrollToTop({ deps: [pathname] });
 *
 * // 2. Scroll a specific scrollable container on tab switch
 * const { targetRef } = useScrollToTop({ deps: [activeTab] });
 * <div ref={targetRef} style={{ overflowY: "auto", height: 400 }}>...</div>
 *
 * // 3. Imperative scroll (e.g., "Back to top" button)
 * const { scrollToTop } = useScrollToTop({ deps: [] });
 * <button onClick={scrollToTop}>↑ Back to top</button>
 *
 * // 4. Instant scroll (no animation)
 * useScrollToTop({ deps: [page], behavior: "instant" });
 *
 * // 5. Delayed scroll (wait for animation/transition to settle)
 * useScrollToTop({ deps: [step], delay: 300 });
 */
export function useScrollToTop({
  deps = [],
  behavior = "smooth",
  delay = 0,
  disabled = false,
  targetRef: externalRef,
}: UseScrollToTopOptions = {}): UseScrollToTopReturn {
  const internalRef = useRef<HTMLElement>(null);
  const targetRef = externalRef ?? internalRef;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getScrollableParent = useCallback((
    element: HTMLElement | null
  ): HTMLElement | Window => {
    if (!element) return window;
    const overflowY = window.getComputedStyle(element).overflowY;
    const isScrollable = overflowY === "auto" || overflowY === "scroll";
    if (isScrollable && element.scrollHeight > element.clientHeight) {
      return element;
    }
    return element.parentElement
      ? getScrollableParent(element.parentElement)
      : window;
  }, []);

  const scrollToTop = useCallback(() => {
    const el = targetRef.current;
    const scrollable = el ? getScrollableParent(el) : window;
    if (scrollable && scrollable !== window) {
      (scrollable as HTMLElement).scrollTo({ top: 0, behavior });
    } else {
      window.scrollTo({ top: 0, behavior });
      document.documentElement.scrollTo({ top: 0, behavior });
      document.body.scrollTo({ top: 0, behavior });
    }
  }, [behavior, targetRef, getScrollableParent]);

  useEffect(() => {
    if (disabled) return;

    if (delay > 0) {
      timerRef.current = setTimeout(scrollToTop, delay);
    } else {
      scrollToTop();
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [disabled, delay, scrollToTop, ...deps]);

  return { scrollToTop, targetRef: internalRef };
}

export default useScrollToTop;